import { useState, useEffect, useRef, useMemo } from 'react';
import mqtt from 'mqtt';
import { InfluxDB } from '@influxdata/influxdb-client';
import { useCardData } from '../components/common/CardContext';
import { processTimeStamp } from './chartUtils';
import { sensorTypeLabels } from "./constants";
import * as cacheUtils from "./cacheUtils";

const INFLUX_URL = process.env.REACT_APP_INFLUX_URL;
const INFLUX_TOKEN = process.env.REACT_APP_INFLUX_TOKEN;  // You'll need to replace this
const INFLUX_ORG = process.env.REACT_APP_INFLUX_ORG;
const INFLUX_BUCKET = process.env.REACT_APP_INFLUX_BUCKET;

const MQTT_STATUS_TOPIC = process.env.REACT_APP_MQTT_STATUS_TOPIC;
const MQTT_AMBIENT_TOPIC = process.env.REACT_APP_MQTT_AMBIENT_TOPIC || '/sensor/ambient/#';
const MQTT_RESPONSE_TOPIC = process.env.REACT_APP_MQTT_RESPONSE_TOPIC;
const MQTT_BROKER_URL = process.env.REACT_APP_MQTT_BROKER_URL || 'wss://websocketcanopyserver.alurellc.com';

const client = new InfluxDB({ url: INFLUX_URL, token: INFLUX_TOKEN });
const queryApi = client.getQueryApi(INFLUX_ORG);

const getCurrentUser = () => cacheUtils.get("userVal", false);

// getMqttAddresses - gets MQTT addresses from either React Query or fallback to CacheManager
export function getMqttAddresses() {
    const userKey = getCurrentUser();
    
    try {
        // Try to get from React Query first
        if (window.__queryClient) {
            const cards = window.__queryClient.getQueryData(['cards', userKey]);
            if (cards && Array.isArray(cards)) {
                // Filter to only get MQTT addresses (starting with 'MQ')
                const mqttAddresses = cards
                    .filter(card => card?.macAddress?.startsWith('MQ'))
                    .map(card => card.macAddress);
                
                console.log('getMqttAddresses from React Query:', {
                    userKey,
                    totalCards: cards.length,
                    mqttCards: mqttAddresses,
                    mqttCardCount: mqttAddresses.length
                });
                
                // For backward compatibility, also store in localStorage
                if (mqttAddresses.length > 0) {
                    cacheUtils.setMqttAddresses(userKey, mqttAddresses);
                }
                
                return mqttAddresses;
            }
        }
        
        // Fallback to localStorage if React Query data isn't available
        const fallbackAddresses = cacheUtils.getMqttAddresses(userKey) || [];
        console.log('getMqttAddresses fallback to localStorage:', {
            userKey,
            fallbackAddresses,
            fallbackCount: fallbackAddresses.length
        });
        return fallbackAddresses;
    } catch (error) {
        console.error('Error getting MQTT addresses:', error);
        // Fallback to localStorage
        return cacheUtils.getMqttAddresses(userKey) || [];
    }
}

// setMqttAddresses - adds an MQTT address and updates both React Query and localStorage
export function setMqttAddresses(mqttAddress) {
    if (!mqttAddress) return [];
    
    const userKey = getCurrentUser();
    let updatedAddresses = [];
    
    try {
        // Update in CacheManager for backward compatibility
        updatedAddresses = cacheUtils.addMqttAddress(userKey, mqttAddress) || [];
        
        // If React Query client is available, update there too
        if (window.__queryClient) {
            // We'll dispatch the event, which will cause components to re-fetch data
            // and update their MQTT address arrays
        }
        
        // Dispatch custom event with safety check
        window.dispatchEvent(new CustomEvent('mqttAddressesChanged', { 
            detail: { addresses: updatedAddresses }
        }));
        
        return updatedAddresses;
    } catch (error) {
        console.error('Error setting MQTT address:', error);
        return updatedAddresses;
    }
}

export function removeMqttAddress(mqttAddress) {
    const userKey = getCurrentUser();
    const mqttAddresses = getMqttAddresses(); 
    if (mqttAddresses) {
        const updatedAddresses = mqttAddresses.filter(address => address !== mqttAddress);
        cacheUtils.setMqttAddresses(userKey, updatedAddresses);
        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('mqttAddressesChanged', { 
            detail: { addresses: updatedAddresses }
        }));
    } else {
        // If no addresses exist, just return an empty array
        cacheUtils.setMqttAddresses(userKey, []);
        window.dispatchEvent(new CustomEvent('mqttAddressesChanged', { 
            detail: { addresses: [] }
        }));
    }
}

export async function fetchHistoricalSensorData(macAddress, sensorType, daysBack = "14d", deviceType="default", aggregateWindow="5m") {
    // 🚨 URGENT DEBUG: Track all InfluxDB historical data calls
    console.log('🚨 fetchHistoricalSensorData called:', {
        macAddress,
        sensorType,
        daysBack,
        deviceType,
        aggregateWindow,
        timestamp: new Date().toISOString(),
        stackTrace: new Error().stack.split('\n').slice(0, 5).join('\n')
    });
    
    let timeUnit = "d"
    let timeValue = daysBack.slice(0, -1) // Default: remove last character
    
    if (daysBack.includes("h")) {
        timeUnit = "h"
        timeValue = daysBack.slice(0, -1)
    } else if (daysBack.includes("w")) {
        timeUnit = "w"
        // Handle both "4w" and "4wk" formats
        if (daysBack.endsWith("wk")) {
            timeValue = daysBack.slice(0, -2) // Remove "wk"
        } else {
            timeValue = daysBack.slice(0, -1) // Remove "w"
        }
    }
    let fluxQuery = ""
    if (deviceType === "sensor") {
        fluxQuery = 
        `
        from(bucket:"${INFLUX_BUCKET}")
            |> range(start: -${timeValue}${timeUnit})
            |> filter(fn: (r) => r._measurement == "${process.env.REACT_APP_SENSOR_MEASUREMENT}")
            |> filter(fn: (r) => r._field == "${sensorType}")
            |> filter(fn: (r) => r.mac_address == "${macAddress}")
            |> aggregateWindow(every: ${aggregateWindow}, fn: mean)
            |> sort(columns: ["_time"])
        `;
    }
    else if (deviceType === "action") {
        fluxQuery = 
        `
        from(bucket:"${INFLUX_BUCKET}")
            |> range(start: -${timeValue}${timeUnit})
            |> filter(fn: (r) => r._measurement == "${process.env.REACT_APP_ACTION_MEASUREMENT}")
            |> filter(fn: (r) => r._field == "${sensorType}")
            |> filter(fn: (r) => r.mac_address == "${macAddress}")
            |> aggregateWindow(every: ${aggregateWindow}, fn: max)
            |> sort(columns: ["_time"])
        `;
    }
    else {
        fluxQuery =
        `
        from(bucket:"${INFLUX_BUCKET}")
            |> range(start: -${timeValue}${timeUnit})
            |> filter(fn: (r) => r._measurement == "mqtt_consumer")
            |> filter(fn: (r) => r._field == "${sensorType}")
            |> filter(fn: (r) => r.mac_address == "${macAddress}")
            |> aggregateWindow(every: ${aggregateWindow}, fn: mean)
            |> sort(columns: ["_time"])
        `;
    }

    try {
        const result = await queryApi.collectRows(fluxQuery);
        return result.map(row => ({
            timestamp: row._time,
            value: row._value
        }));
    } catch (error) {
        console.error('Error fetching historical data:', error);
        return [];
    }
}

export async function fetchLatestSensorData(macAddress) {
    // 🚨 URGENT DEBUG: Track all InfluxDB latest data calls  
    console.log('🚨 fetchLatestSensorData called for:', {
        macAddress,
        timestamp: new Date().toISOString(),
        stackTrace: new Error().stack.split('\n').slice(0, 5).join('\n')
    });
    
    const fluxQuery = `
    from(bucket:"${INFLUX_BUCKET}")
        |> range(start: -5m)
        |> filter(fn: (r) => r._measurement == "mqtt_consumer")
        |> filter(fn: (r) => r.mac_address == "${macAddress}")
        |> last()
`;

    try {
        const result = await queryApi.collectRows(fluxQuery);
        return result.map(row => ({
            timestamp: row._time,
            value: row._value,
            macAddress: row.mac_address
        }))
    }
    catch (error) {
        console.error('Error fetching latest sensor data: ', error);
        return [];
    }
}

export function useMQTTSensorData(macAddress, setSensorData) {
    const { updateCardData } = useCardData();
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);
    const clientRef = useRef(null);
    
    // Get MQTT card MAC addresses directly from React Query
    // This ensures we always have the latest list and don't need a separate storage
    const mqttAddresses = useMemo(() => {
        if (!window.__queryClient) return [];
        
        const userKey = getCurrentUser();
        const cards = window.__queryClient.getQueryData(['cards', userKey]);
        
        if (!cards || !Array.isArray(cards)) return [];
        
        const addresses = cards
            .filter(card => card?.macAddress?.startsWith('MQ'))
            .map(card => card.macAddress);
        
        console.log('useMQTTSensorData - Derived MQTT addresses from cards:', {
            totalCards: cards.length,
            mqttAddresses: addresses,
            mqttCount: addresses.length
        });
        
        return addresses;
    }, []); // Only compute once on mount, rely on React Query cache updates
    
    // Use a ref to track the latest MQTT addresses without causing effect re-runs
    const mqttAddressesRef = useRef(mqttAddresses);

    // Listen for React Query cache updates to keep addresses fresh
    useEffect(() => {
        if (!window.__queryClient) return;
        
            const userKey = getCurrentUser();
        
        // Set up a watcher on the React Query cache
        const unsubscribe = window.__queryClient.getQueryCache().subscribe((event) => {
            // Check if this is a cards query update
            if (event?.query?.queryKey?.[0] === 'cards' && event?.query?.queryKey?.[1] === userKey) {
                const cards = window.__queryClient.getQueryData(['cards', userKey]);
                
                if (cards && Array.isArray(cards)) {
                    const newAddresses = cards
                        .filter(card => card?.macAddress?.startsWith('MQ'))
                        .map(card => card.macAddress);
                    
                    mqttAddressesRef.current = newAddresses;
                    
                    console.log('useMQTTSensorData - MQTT addresses updated from React Query:', {
                        newAddresses,
                        count: newAddresses.length
                    });
                }
            }
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);
    
    console.log('useMQTTSensorData initialized with:', {
        macAddress,
        initialMqttAddresses: mqttAddresses,
        mqttAddressCount: mqttAddresses.length
    });

    useEffect(() => {
        const options = {
            clientId: `react-mqtt-${Math.random().toString(16).slice(3)}`,
            clean: true,
            reconnectPeriod: 5000,
            connectTimeout: 30 * 1000,
            protocolId: 'MQTT',
            protocolVersion: 4
        };

        console.log(`Connecting to MQTT broker: ${MQTT_BROKER_URL}`);
        console.log(`Subscribing to topic: ${MQTT_AMBIENT_TOPIC}`);
        console.log(`Monitoring MAC address: ${macAddress}`);
        console.log('Environment variables:', {
            MQTT_BROKER_URL,
            MQTT_AMBIENT_TOPIC,
            MQTT_STATUS_TOPIC,
            MQTT_RESPONSE_TOPIC
        });

        const client = mqtt.connect(MQTT_BROKER_URL, options);

        client.on('connect', () => {
            console.log('MQTT Connected successfully');
            setIsConnected(true);
            setError(null);
            
            client.subscribe(MQTT_AMBIENT_TOPIC, (err) => {
                if (err) {
                    console.error(`Failed to subscribe to topic ${MQTT_AMBIENT_TOPIC}:`, err);
                    setError(err);
                } else {
                    console.log(`Successfully subscribed to topic: ${MQTT_AMBIENT_TOPIC}`);
                }
            });
        });

        client.on('message', (topic, message) => {
            const msg = message.toString();
            // Parse the message
            const parts = msg.split(',');

            console.log('MQTT Message received:', {
                message: msg,
                parts: parts,
                partsLength: parts.length,
                macAddress: parts[0],
                monitoredAddresses: mqttAddressesRef.current,
                isMonitored: mqttAddressesRef.current && mqttAddressesRef.current.includes(parts[0])
            });
            
            // Check if this MAC address is in our monitored list
            // If the list is empty, we monitor all addresses (for initial setup)
            if(mqttAddressesRef.current && mqttAddressesRef.current.length > 0 && !mqttAddressesRef.current.includes(parts[0])) {
                console.log(`Ignoring message from ${parts[0]} - not in monitored addresses:`, mqttAddressesRef.current);
                return;
            }

            if (parts.length >= 7) {
                console.log(`Received message on topic ${topic}:`, msg);
                const incomingMacAddress = parts[0];
                const temperature = parseFloat(parts[1]) || 0;
                const humidity = parseFloat(parts[2]) || 0;
                //const pressure = parseFloat(parts[3]);
                const moisture = parseFloat(parts[4]) || 0;
                const distance = parseFloat(parts[5]) || 0;   
                const light = parseFloat(parts[6]) || 0;

                const timestamp = processTimeStamp((new Date()).toISOString());

                // Capture message for the MQTT viewer
                const viewerMessage = {
                    id: `${incomingMacAddress}-${Date.now()}`,
                    timestamp: new Date(),
                    messageType: 'sensor',
                    macAddress: incomingMacAddress,
                    friendlyName: 'Sensor Device', // Will be resolved by component using CardContext
                    temperature: temperature,
                    humidity: humidity,
                    moisture: moisture, // Add moisture back since it's available
                    light: light,
                    distance: distance,
                    topic: topic
                };
                globalMqttMessageStore.addMessage(viewerMessage);

                // Update sensor data in the parent component
                if(macAddress === 'all' || incomingMacAddress === macAddress) {
                    console.log(`Updating data for MAC: ${incomingMacAddress}`);

                    // Only update sensorData if we're in a component that needs it
                    if (setSensorData) {
                        setSensorData(prevData => ({
                            ...prevData,
                            temperature: [...(prevData?.temperature || []), {timestamp: new Date(), value: temperature}],
                            humidity: [...(prevData?.humidity || []), {timestamp: new Date(), value: humidity}],
                            moisture: [...(prevData?.moisture || []), {timestamp: new Date(), value: moisture}],
                            distance: [...(prevData?.distance || []), {timestamp: new Date(), value: distance}],
                            light: [...(prevData?.light || []), {timestamp: new Date(), value: light}]
                        }));
                    }

                    // Always update the CardContext with latest values
                    updateCardData(incomingMacAddress, {
                        temperature,
                        humidity,
                        moisture,
                        distance,
                        light,
                        timestamp
                    });
                }
            }
            else {
                console.warn(`Received message ${msg} on topic: ${topic} but it didn't match payload format! Expected at least 7 parts, got ${parts.length}`);
            }
        });

        client.on('error', (err) => {
            console.error('MQTT Connection error:', err);
            setError(err);
            setIsConnected(false);
        });

        client.on('disconnect', () => {
            console.log('MQTT Disconnected');
            setIsConnected(false);
        });

        client.on('reconnect', () => {
            console.log('MQTT Reconnecting...');
            setIsConnected(true);
            setError(null);
            // Resubscribe to topic after reconnection
            client.subscribe(MQTT_AMBIENT_TOPIC, (err) => {
                if (err) {
                    console.error(`Failed to resubscribe to topic ${MQTT_AMBIENT_TOPIC}:`, err);
                    setError(err);
                } else {
                    console.log(`Successfully resubscribed to topic: ${MQTT_AMBIENT_TOPIC}`);
                }
            });
        });

        return () => {
            console.log('Cleaning up MQTT connection');
            client.unsubscribe(MQTT_AMBIENT_TOPIC);
            client.end(true);
        };
    }, [macAddress, setSensorData, updateCardData]);
    // mqttAddressesRef is intentionally omitted from dependencies
    // We use the ref to always access the latest value without causing effect re-runs

    // Return connection status for debugging
    return { isConnected, error };
}

export const parseAmbientData = (message) => {
    //split the message by comma:
    const parts = message.split(',');
    if(parts.length > 0) {
        // Convert temperature from Celsius to Fahrenheit
        const temperatureC = parseFloat(parts[1]);
        const temperatureF = Math.round((temperatureC * 9/5) + 32);
        
        // Constrain humidity between 0-100%
        const humidity = Math.min(100, Math.max(0, parseFloat(parts[2])));
        
        return {
            macAddress: parts[0],
            temperature: temperatureF,
            humidity: humidity,
            pressure: parseFloat(parts[3]),
            moisture: parseFloat(parts[4]),
            battery: parseFloat(parts[5])
        }
    }
    return null;
}

//TODO: we'll have devices store their paired mac address. If the macAddress below matches their pairedAddress, the device will accept the message.
export const publishMQTTStatus = (
    macAddress,
    paired_mac = "X",
    thresholds = {
      humidity: { low: "X", high: "X" },
      temperature: { low: "X", high: "X" },
      water: { low: "X", high: "X" },
      light: { low: "X", high: "X" },
      water2: { low: "X", high: "X" },
    },
    trigger = "X",
    duration = "X",
    sensorTracking = "X",
    acknowledgePing = false
  ) => {
    const deviceMac = normalizePresenceMac(macAddress);
    if (deviceMac) settingsAckInFlight.add(deviceMac);

    const runAttempt = () => new Promise((resolve) => {
      const clientId = `react-mqtt-${Math.random().toString(16).slice(3)}`;
      const responseTopic = `${MQTT_RESPONSE_TOPIC}/${macAddress}`;
      const publishTopic = `${MQTT_STATUS_TOPIC}/${macAddress}`;
      const connectTimeout = 4000;
      const ackTimeout = SETTINGS_ACK_TIMEOUT_MS;

      // Irrigation device expects trigger "0", "0s", or "immediate" for immediate; UI uses "2s"
      const triggerPayload = (trigger === "2s") ? "0s" : trigger;
  
      const message = `${macAddress},${paired_mac},${thresholds?.humidity?.low || 'X'},${thresholds?.humidity?.high || 'X'},${thresholds?.temperature?.low || 'X'},${thresholds?.temperature?.high || 'X'},${thresholds?.water?.low || 'X'},${thresholds?.water?.high || 'X'},${thresholds?.light?.low || 'X'},${thresholds?.light?.high || 'X'},${triggerPayload},${duration},${sensorTracking},${thresholds?.water2?.low || 'X'},${thresholds?.water2?.high || 'X'}`;
  
      const client = mqtt.connect(MQTT_BROKER_URL, {
        clientId,
        clean: true,
        reconnectPeriod: 0,
        connectTimeout,
      });
  
      let timeout = null;
  
      const cleanup = (result) => {
        clearTimeout(timeout);
        client.unsubscribe(responseTopic, () => {
          client.end(true);
          resolve(result);
        });
      };
  
      client.on("connect", () => {
        console.log(`Connected to MQTT broker: ${MQTT_BROKER_URL}`);
        console.log(`Subscribing to topic: ${responseTopic}`);
  
        client.subscribe(responseTopic, { qos: 1 }, (err) => {
          if (err) {
            console.error(`Failed to subscribe to ${responseTopic}:`, err);
            cleanup("failed: subscription error");
            return;
          }
  
          console.log(`Subscribed. Publishing to topic: ${publishTopic}`);
          client.publish(publishTopic, message, { qos: 1 }, (err) => {
            if (err) {
              console.error(`Failed to publish to ${publishTopic}:`, err);
              cleanup("failed: publish error");
              return;
            }
  
            console.log(`Published message: ${message}`);
  
            // Start timeout *after* successful publish. Heartbeat and ping
            // replies stay ignored unless acknowledgePing is set.
            timeout = setTimeout(() => {
              console.warn(`No response received within ${ackTimeout / 1000}s`);
              cleanup("failed: timeout");
            }, ackTimeout);
          });
        });
      });
  
      client.on("message", (topic, msg) => {
        const messageStr = msg.toString();
        console.log(`Received message on ${topic}: ${messageStr}`);
  
        const [receivedMac, status, detail] = messageStr.split(",");
        if (receivedMac === macAddress) {
          const detailKind = (detail || "").trim().toLowerCase();
          // Heartbeats always use status "success" and are not a settings ack.
          // A presence ping is an ack only for a pairing write that leaves other fields unchanged.
          if (status === "success" && detailKind !== "heartbeat" && detailKind !== "ping") {
            cleanup("success");
          } else if (status === "success" && detailKind === "ping" && acknowledgePing) {
            cleanup("accepted-ping");
          } else if (status === "fail") {
            cleanup("failed: device reported failure");
          }
        }
      });
  
      client.on("error", (err) => {
        console.error("MQTT Client error:", err);
        cleanup("failed: connection error");
      });
    });

    // A presence ping already on the wire can consume the device's only
    // incoming packet. Finish that ping, then leave a quiet gap, before
    // the settings write. One retry covers a reply that was still a ping.
    return waitForPresenceIdle(deviceMac)
      .then(() => delay(PRESENCE_QUIET_MS))
      .then(runAttempt)
      .then((result) => (
        result === "failed: timeout"
          ? delay(PRESENCE_QUIET_MS).then(runAttempt)
          : result
      ))
      .finally(() => {
        if (deviceMac) settingsAckInFlight.delete(deviceMac);
      });
};

const PRESENCE_CONNECT_TIMEOUT_MS = 8000;
const PRESENCE_PING_TIMEOUT_MS = 4000;
const SETTINGS_ACK_TIMEOUT_MS = 12000;
/** Quiet period after a presence ping so the device has left its MQTT callback. */
const PRESENCE_QUIET_MS = 800;
/** MACs with a settings write waiting for success,success. Probes skip these. */
const settingsAckInFlight = new Set();
/** Detail pages and other holders that must not be probed. MAC -> count. */
const probePauseCounts = new Map();

function delay(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function isProbeBlocked(mac) {
    return settingsAckInFlight.has(mac) || (probePauseCounts.get(mac) || 0) > 0;
}

function waitForPresenceIdle(mac) {
    if (!mac) return Promise.resolve();
    const waiter = presenceWaiters.get(mac);
    if (!waiter) return Promise.resolve();
    return new Promise((resolve) => {
        const timer = setTimeout(resolve, PRESENCE_PING_TIMEOUT_MS + 250);
        const originalResolve = waiter.resolve;
        waiter.resolve = (result) => {
            clearTimeout(timer);
            originalResolve(result);
            resolve();
        };
    });
}

/** Stop presence probes for this MAC until the returned function is called. */
export function pauseActuatorProbes(macAddress) {
    const mac = normalizePresenceMac(macAddress);
    if (!mac) return () => {};
    probePauseCounts.set(mac, (probePauseCounts.get(mac) || 0) + 1);
    return () => {
        const next = (probePauseCounts.get(mac) || 1) - 1;
        if (next <= 0) probePauseCounts.delete(mac);
        else probePauseCounts.set(mac, next);
    };
}

export function isActuatorProbePaused(macAddress) {
    const mac = normalizePresenceMac(macAddress);
    return Boolean(mac) && (probePauseCounts.get(mac) || 0) > 0;
}

let presenceClient = null;
let presenceConnectPromise = null;
const heartbeatListeners = new Set();
/** Listeners for /sensor/action reports: (actuatorMac, pairedMac) => void */
const actionReportListeners = new Set();
/** In-flight status pings, keyed by normalized MAC. */
const presenceWaiters = new Map();

const normalizePresenceMac = (mac) =>
    typeof mac === "string" ? mac.trim().replace(/;/g, ":").toUpperCase() : "";

const topicRoot = (topic, fallback) => {
    const base = (topic || fallback).replace(/\/#$/, "").replace(/\/$/, "");
    return base;
};

const settlePresenceWaiter = (mac, result) => {
    const waiter = presenceWaiters.get(mac);
    if (!waiter) return;
    clearTimeout(waiter.timer);
    presenceWaiters.delete(mac);
    waiter.resolve(result);
};

const maybeClosePresenceClient = () => {
    if (
        heartbeatListeners.size === 0
        && actionReportListeners.size === 0
        && presenceWaiters.size === 0
        && presenceClient
    ) {
        presenceClient.end(true);
        presenceClient = null;
    }
};

const subscribePresenceTopics = (client) => {
    const responseTopic = `${topicRoot(MQTT_RESPONSE_TOPIC, "/sensor/response")}/#`;
    const actionTopic = "/sensor/action/#";
    client.subscribe(responseTopic, { qos: 0 }, (err) => {
        if (err) {
            console.error(`Failed to subscribe to ${responseTopic}:`, err);
        }
    });
    client.subscribe(actionTopic, { qos: 0 }, (err) => {
        if (err) {
            console.error(`Failed to subscribe to ${actionTopic}:`, err);
        }
    });
};

function handleActionReportMessage(topic, msg) {
    const parts = msg.toString().split(",");
    if (parts.length < 3) return;

    const mac = normalizePresenceMac(parts[0]) || normalizePresenceMac(topic.split("/").pop());
    const paired = normalizePresenceMac(parts[2]);
    if (!mac || !paired) return;

    actionReportListeners.forEach((listener) => listener(mac, paired));
}

function handlePresenceResponseMessage(_topic, msg) {
    const parts = msg.toString().split(",");
    if (parts.length < 2) return;

    const mac = normalizePresenceMac(parts[0]);
    if (!mac) return;

    if (parts[1] === "success") {
        const msgType = parts.length >= 3 ? parts[2].trim().toLowerCase() : "";
        if (msgType === "heartbeat") {
            const seenAt = Date.now();
            heartbeatListeners.forEach((listener) => listener(mac, seenAt));
        }
        if (presenceWaiters.has(mac)) {
            settlePresenceWaiter(mac, "success");
        }
    } else if (parts[1] === "fail" && presenceWaiters.has(mac)) {
        settlePresenceWaiter(mac, "fail");
    }
}

function ensurePresenceClient() {
    if (presenceConnectPromise) {
        return presenceConnectPromise;
    }
    if (presenceClient && presenceClient.connected) {
        return Promise.resolve(presenceClient);
    }

    presenceConnectPromise = new Promise((resolve, reject) => {
        const client = presenceClient || mqtt.connect(MQTT_BROKER_URL, {
            clientId: `react-presence-${Math.random().toString(16).slice(3)}`,
            clean: true,
            reconnectPeriod: 2000,
            connectTimeout: PRESENCE_CONNECT_TIMEOUT_MS,
        });

        if (!presenceClient) {
            presenceClient = client;
            client.on("message", (topic, msg) => {
                if (String(topic).includes("/sensor/action/")) {
                    handleActionReportMessage(topic, msg);
                    return;
                }
                handlePresenceResponseMessage(topic, msg);
            });
            client.on("error", (err) => {
                console.error("Actuator presence MQTT client error:", err);
            });
            client.on("connect", () => {
                subscribePresenceTopics(client);
            });
        }

        const connectTimer = setTimeout(() => {
            reject(new Error("actuator presence connect timeout"));
        }, PRESENCE_CONNECT_TIMEOUT_MS);

        const finish = () => {
            clearTimeout(connectTimer);
            if (
                heartbeatListeners.size === 0
                && actionReportListeners.size === 0
                && presenceWaiters.size === 0
            ) {
                client.end(true);
                presenceClient = null;
                reject(new Error("actuator presence listener removed"));
                return;
            }
            if (client.connected) {
                subscribePresenceTopics(client);
            }
            resolve(client);
        };

        if (client.connected) {
            finish();
        } else {
            client.once("connect", finish);
        }
    }).finally(() => {
        presenceConnectPromise = null;
    });

    return presenceConnectPromise;
}

/**
 * Listen for actuator action reports on /sensor/action/#.
 * Firmware publishes "<actuator>,<proportion>,<paired sensor>,...".
 *
 * @param {(actuatorMac: string, pairedMac: string) => void} onReport
 * @returns {() => void} unsubscribe
 */
export function subscribeActuatorActionReports(onReport) {
    actionReportListeners.add(onReport);
    ensurePresenceClient().catch((err) => {
        if (actionReportListeners.size === 0) return;
        console.error("Actuator action-report MQTT connection failed:", err);
    });
    return () => {
        actionReportListeners.delete(onReport);
        maybeClosePresenceClient();
    };
}

/**
 * Resolve true when a later action report pairs this actuator with expectedPairedMac.
 * Resolves false if that does not happen before timeoutMs.
 *
 * @returns {Promise<boolean>}
 */
export function waitForReportedPair(actuatorMac, expectedPairedMac, timeoutMs = 12000) {
    const mac = normalizePresenceMac(actuatorMac);
    const expected = normalizePresenceMac(expectedPairedMac);
    if (!mac || !expected) {
        return Promise.resolve(false);
    }

    return new Promise((resolve) => {
        let settled = false;
        const finish = (matched) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            unsubscribe();
            resolve(matched);
        };
        const timer = setTimeout(() => finish(false), timeoutMs);
        const unsubscribe = subscribeActuatorActionReports((reportedMac, pairedMac) => {
            if (reportedMac === mac && pairedMac === expected) {
                finish(true);
            }
        });
    });
}

/**
 * Listen for irrigation controller heartbeats on /sensor/response/#.
 * Firmware publishes "<MAC>,success,heartbeat" about every 60 seconds.
 *
 * @param {(mac: string, seenAt: number) => void} onHeartbeat
 * @returns {() => void} unsubscribe
 */
export function subscribeActuatorHeartbeats(onHeartbeat) {
    heartbeatListeners.add(onHeartbeat);
    ensurePresenceClient().catch((err) => {
        if (heartbeatListeners.size === 0) return;
        console.error("Actuator heartbeat MQTT connection failed:", err);
    });
    return () => {
        heartbeatListeners.delete(onHeartbeat);
        maybeClosePresenceClient();
    };
}

/**
 * Ask one actuator if it is reachable without changing its settings.
 * Position 1 is the current paired MAC. Every other field is X.
 * Does not write lastActive or any other stored action field.
 *
 * @returns {Promise<"success"|"fail"|"timeout"|"skipped">}
 */
export function pingActuatorPresence(macAddress, pairedMac) {
    const deviceMac = normalizePresenceMac(macAddress);
    const paired = normalizePresenceMac(pairedMac);
    if (!deviceMac || !paired) {
        return Promise.resolve("fail");
    }
    if (presenceWaiters.has(deviceMac) || isProbeBlocked(deviceMac)) {
        return Promise.resolve("skipped");
    }

    return ensurePresenceClient()
        .then((client) => new Promise((resolve) => {
            if (isProbeBlocked(deviceMac) || presenceWaiters.has(deviceMac)) {
                resolve("skipped");
                return;
            }
            const timer = setTimeout(() => settlePresenceWaiter(deviceMac, "timeout"), PRESENCE_PING_TIMEOUT_MS);
            presenceWaiters.set(deviceMac, { resolve, timer });

            const message = `${deviceMac},${paired},X,X,X,X,X,X,X,X,X,X,X,X,X`;
            const publishTopic = `${topicRoot(MQTT_STATUS_TOPIC, "/sensor/status")}/${deviceMac}`;
            client.publish(publishTopic, message, { qos: 1 }, (err) => {
                if (err) {
                    console.error(`Presence ping failed to publish to ${publishTopic}:`, err);
                    settlePresenceWaiter(deviceMac, "fail");
                }
            });
        }))
        .catch((err) => {
            console.error("Presence MQTT connection failed:", err);
            return "fail";
        });
}

export const generateMqttChartOptions = (title, yAxisLabel) => {
    const yAxisConfig = {
        'Humidity': {
            min: sensorTypeLabels.humidity.min,
            max: sensorTypeLabels.humidity.max,
            title: {
                display: true,
                text: 'Humidity (%)',
                color: 'white'
            }
        },
        'Temperature': {
            min: sensorTypeLabels.temperature.min,
            max: sensorTypeLabels.temperature.max,
            title: {
                display: true,
                text: 'Temperature (°F)',
                color: 'white'
            }
        },
        'Moisture': {
            min: sensorTypeLabels.moisture.min,
            max: sensorTypeLabels.moisture.max,
            title: {
                display: true,
                text: 'Moisture (%)',
                color: 'white'
            }
        },
        'Distance': {
            min: sensorTypeLabels.distance.min,
            max: sensorTypeLabels.distance.max,
            title: {
                display: true,
                text: 'Water Level (in)',
                color: 'white'
            }
        },
        'Distance2': {
            min: sensorTypeLabels.distancetwo.min,
            max: sensorTypeLabels.distancetwo.max,
            title: {
                display: true,
                text: 'Water Level 2 (in)',
                color: 'white'
            }
        },
        'Irrigation': {
            min: sensorTypeLabels.irrigation.min,
            max: sensorTypeLabels.irrigation.max,
            title: {
                display: true,
                text: 'Irrigation (1 = On, 0 = Off)',
                color: 'white'
            }
        },
        'Irrigation2': {
            min: sensorTypeLabels.irrigation2.min,
            max: sensorTypeLabels.irrigation2.max,
            title: {
                display: true,
                text: 'Irrigation 2 (1 = On, 0 = Off)',
                color: 'white'
            }
        },
        'Lux': {
            min: sensorTypeLabels.lux.min,
            max: sensorTypeLabels.lux.max,
            title: {
                display: true,
                text: 'Lux (on/off)',
                color: 'white'
            }
        },
        'Light': {
            min: sensorTypeLabels.light.min,
            max: sensorTypeLabels.light.max,
            title: {
                display: true,
                text: 'Light (on/off)',
                color: 'white'
            }
        }
    }[yAxisLabel] || {
        title: {
            display: true,
            text: yAxisLabel,
            color: 'white'
        }
    };

    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: 'white'
                }
            },
            title: {
                display: true,
                text: title,
                color: 'white'
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'minute',
                    displayFormats: {
                        minute: 'HH:mm'
                    }
                },
                ticks: {
                    color: 'white'
                },
                grid: {
                    color: 'rgba(255,255,255,0.1)'
                }
            },
            y: {
                ...yAxisConfig,
                ticks: {
                    color: 'white'
                },
                grid: {
                    color: 'rgba(255,255,255,0.1)'
                }
            }
        }
    }
};

export const generateMqttChartData = (label, dataPoints, backgroundColor, borderColor) => ({
    labels: dataPoints.map(point => point.timestamp),
    datasets: [{
        label: label,
        data: dataPoints.map(point => ({
            x: new Date(point.timestamp),
            y: point.value
        })),
        backgroundColor: backgroundColor || 'transparent', 
        borderColor: borderColor,
        borderWidth: 1, 
        fill: false, 
        tension: 0.1,  
        pointBackgroundColor: borderColor,
        pointBorderColor: borderColor,
        pointHoverBackgroundColor: borderColor,
        pointHoverBorderColor: borderColor,
        pointRadius: dataPoints.length > 500 ? 1 : 2, 
        pointHoverRadius: 4 
    }]
});

export const generateSkeletonChart = (sensorType) => {
    // Create skeleton chart when no data is available
    const skeletonOptions = generateMqttChartOptions(
        `${sensorType} Sensor Data`, 
        sensorType
    );
    // Set up time range for the default last 14 days
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 14);

    let minValue = 0;
    let maxValue = 0;

    if (sensorType in sensorTypeLabels) {
        minValue = sensorTypeLabels[sensorType].min;
        maxValue = sensorTypeLabels[sensorType].max;
    }

    // Configure x-axis for time range
    skeletonOptions.scales.x = {
        type: 'time',
        display: true,
        time: {
            unit: 'day',
            displayFormats: {
                day: 'MM/dd'
            }
        },
        min: startDate,
        max: endDate,
        ticks: {
            color: 'white'
        },
        grid: {
            color: 'rgba(255,255,255,0.1)'
        }
    };

    // Configure y-axis with appropriate range
    skeletonOptions.scales.y = {
        display: true,
        min: minValue,
        max: maxValue,
        ticks: {
            color: 'white'
        },
        grid: {
            color: 'rgba(255,255,255,0.1)'
        }
    };
    
    // Create empty dataset with the right color
    const skeletonData = {
        labels: [],
        datasets: [
            {
                label: `${sensorType} (No Data)`,
                data: [],
                backgroundColor: 'transparent',
                borderColor: 'rgba(255,255,255,0.3)',
                borderWidth: 1,
                pointRadius: 0,
                borderDash: [5, 5], // Dashed line to indicate it's a placeholder
                fill: false
            }
        ]
    };
    
    return {skeletonData, skeletonOptions};
}

// Global message store for MQTT messages
const globalMqttMessageStore = {
  messages: [],
  listeners: new Set(),
  
  addMessage(message) {
    this.messages = [message, ...this.messages.slice(0, 63)]; // Keep last 64 messages
    this.listeners.forEach(listener => listener(this.messages));
  },
  
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  },
  
  getMessages() {
    return this.messages;
  }
};

// Export the global store for use in components
export { globalMqttMessageStore };